import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clearDocument } from 'y-indexeddb';
import { AdminPanel } from '../admin/AdminPanel';
import { AuditTab } from '../admin/AuditTab';
import { useAuth } from '../auth/AuthContext';
import {
  ApiError,
  api,
  type BoardBlock,
  type BoardSummary,
  type CalendarEvent,
  type EventInput,
  type Organization,
} from '../lib/api';
import {
  lmsApi,
  type CalendarTaskDue,
  type MemberKind,
  type MyTaskItem,
  type RosterMember,
  type StudentSummary,
  type TaskInput,
  type TeacherTask,
} from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { BoardsView } from './BoardsView';
import { CalendarView } from './CalendarView';
import { DayModal } from './DayModal';
import { EventEditor } from './EventEditor';
import { HomeworkView } from './HomeworkView';
import { MyTasksView } from './MyTasksView';
import { OrgSidebar, type DashboardView } from './OrgSidebar';
import { PeopleView } from './PeopleView';
import { GroupsView } from './GroupsView';
import { PersonProfileModal, type PaymentPatch } from './PersonProfileModal';
import { TaskEditor } from './TaskEditor';
import { TaskModal, type TaskModalTask } from './TaskModal';
import { endOfDay, eventOnDay, isSameDay, monthGrid, startOfDay, startOfMonth } from './dates';
import { useReminders } from './useReminders';

const LAST_ORG_KEY = 'kanvas.lastOrg';

function readLastOrg(): string | null {
  try {
    return localStorage.getItem(LAST_ORG_KEY);
  } catch {
    return null;
  }
}

function writeLastOrg(id: string): void {
  try {
    localStorage.setItem(LAST_ORG_KEY, id);
  } catch {
    // Private mode; the dashboard will just default to the first organisation.
  }
}

export interface DashboardProps {
  onOpenBoard: (id: string) => void;
}

/**
 * The range of deadlines a month view needs: from the start of its first cell
 * to the end of its last one, neighbouring months' days included — a task due
 * at 18:00 on the last visible day is still on the grid.
 */
function dueWindow(month: Date): { from: string; to: string } {
  const cells = monthGrid(month);
  return {
    from: startOfDay(cells[0]!).toISOString(),
    to: endOfDay(cells[cells.length - 1]!).toISOString(),
  };
}

/**
 * A teacher's student, shaped like a roster row.
 *
 * The payment fields stay at their empty defaults on purpose: only the owner
 * can see or set them, and the teacher's profile card hides that section
 * entirely rather than showing a plausible-looking "не оплачено".
 */
function asRosterRow(orgId: string, student: StudentSummary): RosterMember {
  return {
    ...student,
    orgId,
    role: 'member',
    kind: 'student',
    paid: false,
    lessonsCount: null,
    paymentAmount: null,
    paidAt: null,
    teacherIds: [],
    studentIds: [],
  };
}

/**
 * Home screen: organisations on the left, their boards or calendar on the
 * right.
 *
 * All server state lives here and is passed down, rather than each view
 * fetching for itself. Boards and events are not independent — deleting a
 * board unlinks it from events, an event links to a board by name — so one
 * owner of the data and one reload path is simpler to keep correct than
 * several caches agreeing with each other.
 */
export function Dashboard({ onOpenBoard }: DashboardProps) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState<string | null>(readLastOrg);
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [blocks, setBlocks] = useState<BoardBlock[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  // ---- tutoring layer ----------------------------------------------------
  /** Whole roster for the owner; a teacher's own students for a teacher. */
  const [roster, setRoster] = useState<RosterMember[]>([]);
  /** Homework as its authors see it — the owner's copy covers the whole org. */
  const [tasks, setTasks] = useState<TeacherTask[]>([]);
  /** A student's own assignments. */
  const [myTasks, setMyTasks] = useState<MyTaskItem[]>([]);
  const [taskDues, setTaskDues] = useState<CalendarTaskDue[]>([]);

  const [view, setView] = useState<DashboardView>('boards');
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [editing, setEditing] = useState<{
    event: CalendarEvent | null;
    day: Date;
  } | null>(null);
  /** Day opened from the month grid. */
  const [dayModal, setDayModal] = useState<Date | null>(null);
  /** Task opened as a full window, from any list or the calendar. */
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  /** Person whose profile card is open. */
  const [profileId, setProfileId] = useState<string | null>(null);
  const [taskEditor, setTaskEditor] = useState<{
    task: TeacherTask | null;
    studentIds?: string[];
  } | null>(null);

  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reminders = useReminders(events);

  /*
   * Which tutoring endpoints are worth calling depends on this account's role
   * in the organisation, which lives in the orgs list. A ref rather than the
   * state itself keeps `refreshContent` stable — depending on `orgs` would
   * make every organisation refresh re-run the content load behind it.
   */
  const orgsRef = useRef<Organization[]>([]);
  useEffect(() => {
    orgsRef.current = orgs;
  }, [orgs]);

  /*
   * Responses arrive in whatever order the network likes. Without these, a
   * slow answer for the organisation just left would overwrite the boards,
   * homework and roster of the one now on screen; and an older reload of the
   * same organisation could undo a newer one.
   */
  const latestOrgRef = useRef<string | null>(orgId);
  const contentSeq = useRef(0);
  const duesSeq = useRef(0);
  /** The month on screen, for the deadline window of a content reload. */
  const monthRef = useRef(month);
  const monthLoaded = useRef(false);
  useEffect(() => {
    monthRef.current = month;
  }, [month]);

  const report = useCallback((e: unknown, fallback: string) => {
    if (e instanceof ApiError && e.code === 'unreachable') {
      setError(e.message);
      return;
    }
    setError(e instanceof Error ? `${fallback}: ${e.message}` : fallback);
  }, []);

  // ---- loading -----------------------------------------------------------

  const refreshOrgs = useCallback(async (preferId?: string) => {
    const list = await api.listOrgs();
    setOrgs(list);

    // Keep the current selection when it still exists, so a rename or a
    // board change does not bounce the user to another organisation.
    setOrgId((current) => {
      const wanted = preferId ?? current;
      if (wanted && list.some((org) => org.id === wanted)) return wanted;
      return list[0]?.id ?? null;
    });
    return list;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshOrgs();
      } catch (e) {
        if (!cancelled) report(e, 'Не удалось загрузить организации');
      } finally {
        if (!cancelled) setLoadingOrgs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshOrgs, report]);

  const refreshContent = useCallback(async (id: string) => {
    /*
     * Every event of the organisation, not just the visible month: the
     * reminder scheduler has to see what is coming regardless of which month
     * the user happens to be looking at. The API accepts a range for when a
     * calendar grows large enough to need one.
     */
    const org = orgsRef.current.find((item) => item.id === id);
    const isOwner = org?.myRole === 'admin';
    const isTeacher = org?.myKind === 'teacher';
    const isCurator = org?.myKind === 'curator';
    const maySeeRoster = isOwner || isTeacher || isCurator;

    const seq = ++contentSeq.current;
    const dueSeq = ++duesSeq.current;
    const { from, to } = dueWindow(monthRef.current);

    /*
     * The secondary lists must not sink the whole load, so each falls back to
     * empty — but the failure is remembered and reported, or an empty list
     * would read as "Заданий пока нет" when the truth is "не загрузилось".
     */
    let failure: unknown = null;
    const soft = <T,>(promise: Promise<T>, fallback: T): Promise<T> =>
      promise.catch((e: unknown) => {
        failure ??= e;
        return fallback;
      });

    const [boardList, blockList, eventList, rosterList, myStudentList, taskList, myTaskList, dues] =
      await Promise.all([
        api.listBoards(id),
        soft(api.listBlocks(id), [] as BoardBlock[]),
        soft(api.listEvents(id), [] as CalendarEvent[]),
        maySeeRoster ? soft(lmsApi.listRoster(id), [] as RosterMember[]) : Promise.resolve(null),
        isTeacher && !maySeeRoster
          ? soft(lmsApi.myStudents(id), [] as StudentSummary[])
          : Promise.resolve(null),
        isOwner || isTeacher
          ? soft(lmsApi.listTasks(id), [] as TeacherTask[])
          : Promise.resolve([] as TeacherTask[]),
        soft(lmsApi.myTasks(id), [] as MyTaskItem[]),
        soft(lmsApi.tasksCalendar(id, from, to), [] as CalendarTaskDue[]),
      ]);

    // Stale: the user moved to another organisation, or a newer reload of this
    // one is already on its way.
    if (latestOrgRef.current !== id || seq !== contentSeq.current) return;

    setBoards(boardList);
    setBlocks(blockList);
    setEvents(eventList);
    setRoster(rosterList ?? (myStudentList ?? []).map((student) => asRosterRow(id, student)));
    setTasks(taskList);
    setMyTasks(myTaskList);
    // The month may have been paged while this was in flight.
    if (dueSeq === duesSeq.current) setTaskDues(dues);
    if (failure) report(failure, 'Не удалось загрузить часть данных');
  }, [report]);

  useEffect(() => {
    latestOrgRef.current = orgId;
    if (!orgId) {
      setBoards([]);
      setBlocks([]);
      setEvents([]);
      setRoster([]);
      setTasks([]);
      setMyTasks([]);
      setTaskDues([]);
      return;
    }
    writeLastOrg(orgId);

    let cancelled = false;
    setLoadingContent(true);
    (async () => {
      try {
        await refreshContent(orgId);
      } catch (e) {
        if (!cancelled) report(e, 'Не удалось загрузить содержимое');
      } finally {
        if (!cancelled) setLoadingContent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, refreshContent, report]);

  // An invitee appears in the roster only after they accept — which happens
  // in another browser. Opening «Участники» or homework reloads so new people
  // and freshly assigned work show up without a full page refresh.
  useEffect(() => {
    if (!orgId) return;
    if (view !== 'people' && view !== 'groups' && view !== 'myTasks' && view !== 'homework') return;
    void refreshContent(orgId).catch(() => undefined);
  }, [view, orgId, refreshContent]);

  /*
   * Deadlines are fetched for the month on screen — paging to another month
   * reloads them, since the content load above only covers the month that was
   * open when it ran. (A change of organisation goes through that load.)
   */
  useEffect(() => {
    // The first run coincides with the initial content load, which covers it.
    if (!monthLoaded.current) {
      monthLoaded.current = true;
      return;
    }
    const id = latestOrgRef.current;
    if (!id) return;
    const seq = ++duesSeq.current;
    const { from, to } = dueWindow(month);
    lmsApi
      .tasksCalendar(id, from, to)
      .then((dues) => {
        if (latestOrgRef.current === id && seq === duesSeq.current) setTaskDues(dues);
      })
      .catch((e: unknown) => {
        if (latestOrgRef.current === id && seq === duesSeq.current) {
          report(e, 'Не удалось загрузить сроки заданий');
        }
      });
  }, [month, report]);

  const reload = useCallback(async () => {
    try {
      const list = await refreshOrgs();
      const active = orgId && list.some((o) => o.id === orgId) ? orgId : list[0]?.id;
      if (active) await refreshContent(active);
      setError(null);
    } catch (e) {
      report(e, 'Не удалось обновить данные');
    }
  }, [orgId, refreshContent, refreshOrgs, report]);

  // ---- organisations -----------------------------------------------------

  const createOrg = useCallback(
    async (name: string) => {
      try {
        const org = await api.createOrg(name);
        await refreshOrgs(org.id);
        setView('boards');
      } catch (e) {
        report(e, 'Не удалось создать организацию');
      }
    },
    [refreshOrgs, report],
  );

  const renameOrg = useCallback(
    async (id: string, name: string) => {
      try {
        await api.updateOrg(id, { name });
        await refreshOrgs();
      } catch (e) {
        report(e, 'Не удалось переименовать организацию');
      }
    },
    [refreshOrgs, report],
  );

  const deleteOrg = useCallback(
    async (id: string) => {
      const org = orgs.find((o) => o.id === id);
      const boardCount = org?.boardCount ?? 0;
      const confirmed = window.confirm(
        boardCount > 0
          ? `Удалить «${org?.name}»? Вместе с ней удалятся ${boardCount} доск(и) и все события. Это нельзя отменить.`
          : `Удалить «${org?.name}»?`,
      );
      if (!confirmed) return;

      try {
        await api.deleteOrg(id);
        await refreshOrgs();
      } catch (e) {
        report(e, 'Не удалось удалить организацию');
      }
    },
    [orgs, refreshOrgs, report],
  );

  // ---- boards ------------------------------------------------------------

  const createBoard = useCallback(
    async (name: string, blockId?: string | null) => {
      if (!orgId) return;
      try {
        const board = await api.createBoard(name, orgId);
        // Created from inside a block, it belongs to that block — otherwise it
        // would quietly appear one level up, where nobody is looking. Filing is
        // a nicety though, so a refusal must not sink the board itself.
        if (blockId) await api.updateBoard(board.id, { blockId }).catch(() => undefined);
        // Straight into the new board: creating one is always a prelude to
        // drawing on it.
        onOpenBoard(board.id);
      } catch (e) {
        // A student gets exactly one board, and hitting that limit is a rule
        // rather than a failure — so it is explained, not reported as an error.
        if (e instanceof ApiError && e.code === 'board_quota') {
          setError('У вас уже есть своя доска — вторую создать нельзя.');
          return;
        }
        report(e, 'Не удалось создать доску');
      }
    },
    [onOpenBoard, orgId, report],
  );

  const renameBoard = useCallback(
    async (id: string, name: string) => {
      try {
        await api.updateBoard(id, { name });
        await reload();
      } catch (e) {
        report(e, 'Не удалось переименовать доску');
      }
    },
    [reload, report],
  );

  const moveBoard = useCallback(
    async (id: string, targetOrgId: string) => {
      try {
        await api.updateBoard(id, { orgId: targetOrgId });
        await reload();
      } catch (e) {
        report(e, 'Не удалось переместить доску');
      }
    },
    [reload, report],
  );

  const setBoardBlock = useCallback(
    async (id: string, blockId: string | null) => {
      try {
        await api.updateBoard(id, { blockId });
        await reload();
      } catch (e) {
        report(e, 'Не удалось переместить доску');
      }
    },
    [reload, report],
  );

  // ---- board blocks ------------------------------------------------------

  const createBlock = useCallback(
    async (name: string) => {
      if (!orgId) return;
      try {
        await api.createBlock(orgId, name);
        await reload();
      } catch (e) {
        report(e, 'Не удалось создать блок');
      }
    },
    [orgId, reload, report],
  );

  const renameBlock = useCallback(
    async (id: string, name: string) => {
      if (!orgId) return;
      try {
        await api.updateBlock(orgId, id, { name });
        await reload();
      } catch (e) {
        report(e, 'Не удалось переименовать блок');
      }
    },
    [orgId, reload, report],
  );

  const reorderBlocks = useCallback(
    async (orderedIds: string[]) => {
      if (!orgId) return;
      // Optimistic, because a drag that snaps back for a round trip reads as
      // "the drop did not work".
      setBlocks((current) =>
        [...current]
          .sort((a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id))
          .map((block, index) => ({ ...block, order: index })),
      );
      try {
        for (const [index, id] of orderedIds.entries()) {
          await api.updateBlock(orgId, id, { order: index });
        }
      } catch (e) {
        report(e, 'Не удалось изменить порядок блоков');
        await reload();
      }
    },
    [orgId, reload, report],
  );

  const deleteBlock = useCallback(
    async (id: string) => {
      if (!orgId) return;
      const block = blocks.find((b) => b.id === id);
      // Worth spelling out, since "удалить блок" could easily be read as
      // "удалить доски внутри".
      if (!window.confirm(`Удалить блок «${block?.name}»? Доски останутся, но станут без блока.`)) {
        return;
      }
      try {
        await api.deleteBlock(orgId, id);
        await reload();
      } catch (e) {
        report(e, 'Не удалось удалить блок');
      }
    },
    [blocks, orgId, reload, report],
  );

  const deleteBoard = useCallback(
    async (id: string) => {
      const board = boards.find((b) => b.id === id);
      if (!window.confirm(`Удалить доску «${board?.name}»? Это нельзя отменить.`)) return;
      try {
        await api.deleteBoard(id);
        await clearDocument(`kanvas:${id}`).catch(() => undefined);
        await reload();
      } catch (e) {
        report(e, 'Не удалось удалить доску');
      }
    },
    [boards, reload, report],
  );

  // ---- calendar ----------------------------------------------------------

  const saveEvent = useCallback(
    async (input: EventInput, id: string | null) => {
      if (id) await api.updateEvent(id, input);
      else await api.createEvent(input);
      if (orgId) await refreshContent(orgId);
    },
    [orgId, refreshContent],
  );

  const deleteEvent = useCallback(
    async (id: string) => {
      try {
        await api.deleteEvent(id);
        if (orgId) await refreshContent(orgId);
      } catch (e) {
        report(e, 'Не удалось удалить событие');
      }
    },
    [orgId, refreshContent, report],
  );

  /**
   * Drag-and-drop reschedule. The time of day and the length are kept — a
   * lesson dragged to Friday is the same lesson at the same hour, and asking
   * for the time again after every drag would make moving a week's plan
   * unbearable.
   */
  const moveEvent = useCallback(
    async (eventId: string, day: Date) => {
      const event = events.find((item) => item.id === eventId);
      if (!event) return;
      const oldStart = new Date(event.start);
      const duration = new Date(event.end).getTime() - oldStart.getTime();
      const start = new Date(day);
      start.setHours(oldStart.getHours(), oldStart.getMinutes(), 0, 0);
      const end = new Date(start.getTime() + duration);
      if (start.getTime() === oldStart.getTime()) return;

      const patch = { start: start.toISOString(), end: end.toISOString() };
      // Optimistic: a chip that flies back to the old day and then jumps reads
      // as a failed drop.
      setEvents((current) =>
        current.map((item) => (item.id === eventId ? { ...item, ...patch } : item)),
      );
      try {
        await api.updateEvent(eventId, patch);
      } catch (e) {
        report(e, 'Не удалось перенести событие');
        if (orgId) await refreshContent(orgId);
      }
    },
    [events, orgId, refreshContent, report],
  );

  const moveTaskDue = useCallback(
    async (taskId: string, day: Date) => {
      const due = taskDues.find((item) => item.id === taskId);
      if (!due) return;
      const old = new Date(due.dueAt);
      const next = new Date(day);
      next.setHours(old.getHours(), old.getMinutes(), 0, 0);
      if (next.getTime() === old.getTime()) return;

      const dueAt = next.toISOString();
      setTaskDues((current) =>
        current.map((item) => (item.id === taskId ? { ...item, dueAt } : item)),
      );
      setTasks((current) =>
        current.map((item) => (item.id === taskId ? { ...item, dueAt } : item)),
      );
      try {
        if (!orgId) return;
        await lmsApi.updateTask(orgId, taskId, { dueAt });
      } catch (e) {
        report(e, 'Не удалось перенести срок');
        if (orgId) await refreshContent(orgId);
      }
    },
    [orgId, refreshContent, report, taskDues],
  );

  // ---- tutoring ----------------------------------------------------------

  const setMemberKind = useCallback(
    async (userId: string, kind: MemberKind) => {
      if (!orgId) return;
      try {
        await lmsApi.setMemberKind(orgId, userId, kind);
        await refreshContent(orgId);
      } catch (e) {
        report(e, 'Не удалось изменить роль');
      }
    },
    [orgId, refreshContent, report],
  );

  const setStudentTeachers = useCallback(
    async (studentId: string, teacherIds: string[]) => {
      if (!orgId) return;
      setRoster((current) =>
        current.map((m) => (m.userId === studentId ? { ...m, teacherIds } : m)),
      );
      try {
        await lmsApi.setStudentTeachers(orgId, studentId, teacherIds);
        await refreshContent(orgId);
      } catch (e) {
        report(e, 'Не удалось закрепить учителя');
        await refreshContent(orgId);
      }
    },
    [orgId, refreshContent, report],
  );

  const setTeacherStudents = useCallback(
    async (teacherId: string, studentIds: string[]) => {
      if (!orgId) return;
      setRoster((current) =>
        current.map((m) => (m.userId === teacherId ? { ...m, studentIds } : m)),
      );
      try {
        await lmsApi.setTeacherStudents(orgId, teacherId, studentIds);
        await refreshContent(orgId);
      } catch (e) {
        report(e, 'Не удалось закрепить учеников');
        await refreshContent(orgId);
      }
    },
    [orgId, refreshContent, report],
  );

  const setPayment = useCallback(
    async (userId: string, patch: PaymentPatch) => {
      if (!orgId) return;
      // Optimistic: the owner ticks through a list of students, and a round
      // trip per tick would make the whole card feel unresponsive.
      setRoster((current) => current.map((m) => (m.userId === userId ? { ...m, ...patch } : m)));
      try {
        const updated = await lmsApi.setMemberPayment(orgId, userId, patch);
        // The server stamps today's date when "paid" is first ticked, so the
        // card has to take its answer rather than keep the guess.
        setRoster((current) =>
          current.map((m) =>
            m.userId === userId
              ? {
                  ...m,
                  paid: updated.paid,
                  paidAt: updated.paidAt,
                  lessonsCount: updated.lessonsCount,
                  paymentAmount: updated.paymentAmount,
                }
              : m,
          ),
        );
      } catch (e) {
        report(e, 'Не удалось сохранить оплату');
        await refreshContent(orgId);
      }
    },
    [orgId, refreshContent, report],
  );

  const saveTask = useCallback(
    async (input: TaskInput, taskId: string | null) => {
      if (!orgId) return;
      if (taskId) await lmsApi.updateTask(orgId, taskId, input);
      else await lmsApi.createTask(orgId, input);
      await refreshContent(orgId);
    },
    [orgId, refreshContent],
  );

  const deleteTask = useCallback(
    async (taskId: string) => {
      if (!orgId) return;
      try {
        await lmsApi.deleteTask(orgId, taskId);
        setOpenTaskId((current) => (current === taskId ? null : current));
        await refreshContent(orgId);
      } catch (e) {
        report(e, 'Не удалось удалить задание');
      }
    },
    [orgId, refreshContent, report],
  );

  const setAssignmentDone = useCallback(
    async (item: MyTaskItem, done: boolean) => {
      if (!orgId || !user) return;
      setMyTasks((current) =>
        current.map((i) =>
          i.assignmentId === item.assignmentId ? { ...i, done, hasSubmission: done || i.hasSubmission } : i,
        ),
      );
      setTaskDues((current) =>
        current.map((due) => (due.id === item.task.id ? { ...due, done } : due)),
      );
      try {
        const saved = await lmsApi.setAssignmentDone(orgId, item.task.id, user.id, done);
        // Unticking does not undo a teacher's acceptance, so take the answer.
        setMyTasks((current) =>
          current.map((i) =>
            i.assignmentId === item.assignmentId
              ? { ...i, done: saved.done, accepted: saved.accepted }
              : i,
          ),
        );
        setTaskDues((current) =>
          current.map((due) => {
            if (due.id !== item.task.id) return due;
            const students = due.students.map((s) =>
              s.userId === user.id
                ? { ...s, done: saved.done, needsReview: saved.needsReview }
                : s,
            );
            return {
              ...due,
              done: saved.done,
              students,
              awaitingReview: students.some((s) => s.needsReview),
            };
          }),
        );
      } catch (e) {
        report(e, 'Не удалось отметить задание');
        await refreshContent(orgId);
      }
    },
    [orgId, refreshContent, report, user],
  );

  const patchMySubmission = useCallback((assignmentId: string, patch: Partial<MyTaskItem>) => {
    setMyTasks((current) =>
      current.map((item) => (item.assignmentId === assignmentId ? { ...item, ...patch } : item)),
    );
  }, []);

  const toggleTask = useCallback(
    async (event: CalendarEvent, taskId: string, done: boolean) => {
      const nextTasks = event.tasks.map((task) => (task.id === taskId ? { ...task, done } : task));
      // Optimistic: a checkbox that waits for a round trip feels broken.
      setEvents((current) =>
        current.map((item) => (item.id === event.id ? { ...item, tasks: nextTasks } : item)),
      );
      try {
        await api.updateEvent(event.id, { tasks: nextTasks });
      } catch (e) {
        report(e, 'Не удалось сохранить задачу');
        if (orgId) await refreshContent(orgId);
      }
    },
    [orgId, refreshContent, report],
  );

  const toggleEventDone = useCallback(
    async (event: CalendarEvent, done: boolean) => {
      setEvents((current) =>
        current.map((item) =>
          item.id === event.id ? { ...item, done, doneAt: done ? Date.now() : null } : item,
        ),
      );
      try {
        await api.updateEvent(event.id, { done });
      } catch (e) {
        report(e, 'Не удалось отметить событие');
        if (orgId) await refreshContent(orgId);
      }
    },
    [orgId, refreshContent, report],
  );

  const toggleHomeworkStudent = useCallback(
    async (taskId: string, studentId: string, done: boolean) => {
      if (!orgId) return;
      // A student ticking their own deadline in the calendar hands the work in
      // for review — a different thing from a teacher accepting it.
      if (studentId === user?.id) {
        const mine = myTasks.find((item) => item.task.id === taskId);
        if (mine) {
          await setAssignmentDone(mine, done);
          return;
        }
      }
      setTasks((current) =>
        current.map((task) =>
          task.id !== taskId
            ? task
            : {
                ...task,
                assignments: task.assignments.map((a) =>
                  a.userId === studentId
                    ? { ...a, done, doneAt: done ? Date.now() : null, needsReview: done ? false : a.needsReview }
                    : a,
                ),
              },
        ),
      );
      setTaskDues((current) =>
        current.map((due) => {
          if (due.id !== taskId) return due;
          const students = due.students.map((s) =>
            s.userId === studentId ? { ...s, done, needsReview: done ? false : s.needsReview } : s,
          );
          return {
            ...due,
            students,
            done: students.length > 0 && students.every((s) => s.done),
            awaitingReview: students.some((s) => s.needsReview),
          };
        }),
      );
      setMyTasks((current) =>
        current.map((item) =>
          item.task.id === taskId && user?.id === studentId
            ? { ...item, done, hasSubmission: done || item.hasSubmission }
            : item,
        ),
      );
      try {
        const saved = await lmsApi.setAssignmentDone(orgId, taskId, studentId, done);
        // Un-accepting work that was handed in puts it back in the review
        // queue, which only the server's answer can tell.
        setTasks((current) =>
          current.map((task) =>
            task.id !== taskId
              ? task
              : {
                  ...task,
                  assignments: task.assignments.map((a) =>
                    a.userId === studentId ? { ...a, needsReview: saved.needsReview } : a,
                  ),
                },
          ),
        );
        setTaskDues((current) =>
          current.map((due) => {
            if (due.id !== taskId) return due;
            const students = due.students.map((s) =>
              s.userId === studentId ? { ...s, needsReview: saved.needsReview } : s,
            );
            return { ...due, students, awaitingReview: students.some((s) => s.needsReview) };
          }),
        );
      } catch (e) {
        report(e, 'Не удалось отметить задание');
        await refreshContent(orgId);
      }
    },
    [myTasks, orgId, refreshContent, report, setAssignmentDone, user],
  );

  // ---- render ------------------------------------------------------------

  const activeOrg = orgs.find((org) => org.id === orgId) ?? null;
  const isOwner = activeOrg?.myRole === 'admin';
  const isTeacher = activeOrg?.myKind === 'teacher';
  const isStudent = activeOrg?.myKind === 'student';

  /** Whoever may set homework may also plan in the calendar. */
  const canCreateEvent = Boolean(isOwner || isTeacher);
  const canEditEvent = useCallback(
    (event: CalendarEvent) => Boolean(isOwner || (event.createdBy && event.createdBy === user?.id)),
    [isOwner, user?.id],
  );

  /** Who the task editor may assign to: the whole org for the owner, their own for a teacher. */
  const assignableStudents = useMemo<StudentSummary[]>(
    () =>
      roster
        .filter((member) => member.kind === 'student')
        .map(({ userId, name, email, color }) => ({ userId, name, email, color })),
    [roster],
  );

  const knownSubjects = useMemo(() => {
    const names = new Set<string>();
    for (const task of tasks) if (task.subject) names.add(task.subject);
    return [...names].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [tasks]);

  /*
   * One task window serves everybody, so the task is resolved from whichever
   * list this account actually has: a student's own assignments, or the
   * authored tasks a teacher and the owner see.
   */
  const openTask = useMemo<{
    task: TaskModalTask;
    myDone: boolean | null;
    item: MyTaskItem | null;
    editable: TeacherTask | null;
  } | null>(() => {
    if (!openTaskId) return null;
    const authored = tasks.find((task) => task.id === openTaskId);
    if (authored) return { task: authored, myDone: null, item: null, editable: authored };
    const mine = myTasks.find((item) => item.task.id === openTaskId);
    if (mine) {
      return {
        task: { ...mine.task, teacherName: mine.teacher?.name ?? null },
        myDone: mine.done,
        item: mine,
        editable: null,
      };
    }
    return null;
  }, [myTasks, openTaskId, tasks]);

  const profilePerson = roster.find((member) => member.userId === profileId) ?? null;

  const dayModalEvents = dayModal ? events.filter((event) => eventOnDay(event, dayModal)) : [];
  const dayModalDues = dayModal
    ? taskDues.filter((due) => isSameDay(new Date(due.dueAt), dayModal))
    : [];

  return (
    <div className="dash">
      <OrgSidebar
        orgs={orgs}
        activeOrgId={orgId}
        view={view}
        onSelectOrg={setOrgId}
        onSelectView={setView}
        onCreateOrg={createOrg}
        onRenameOrg={renameOrg}
        onDeleteOrg={deleteOrg}
        // Removing someone or changing their role alters the roster and the
        // homework assigned to them, neither of which the popover owns.
        onMembersChanged={() => void reload()}
      />

      <main className="dash-main">
        {error && (
          <div className="dash-error" role="alert">
            <Icon name="close" size={14} />
            <span>{error}</span>
            <button type="button" className="dash-error-retry" onClick={() => void reload()}>
              Повторить
            </button>
            <button type="button" onClick={() => setError(null)} aria-label="Скрыть">
              <Icon name="close" size={14} />
            </button>
          </div>
        )}

        {loadingOrgs && <div className="dash-loading">Загрузка…</div>}

        {!loadingOrgs && !activeOrg && view !== 'admin' && (
          <div className="dash-empty">
            <Icon name="org" size={28} />
            <h2>Нет организаций</h2>
            <p>Создайте организацию в левой панели, чтобы начать.</p>
          </div>
        )}

        {activeOrg && view === 'boards' && (
          <BoardsView
            /* Switching organisations starts at the top level again, rather
               than inside a block that belonged to the previous one. */
            key={activeOrg.id}
            org={activeOrg}
            orgs={orgs}
            boards={boards}
            blocks={blocks}
            loading={loadingContent}
            onCreateBoard={createBoard}
            onOpenBoard={onOpenBoard}
            onRenameBoard={renameBoard}
            onMoveBoard={moveBoard}
            onSetBoardBlock={setBoardBlock}
            onDeleteBoard={deleteBoard}
            onCreateBlock={createBlock}
            onRenameBlock={renameBlock}
            onDeleteBlock={deleteBlock}
            onReorderBlocks={reorderBlocks}
          />
        )}

        {activeOrg && view === 'people' && (
          <PeopleView
            org={activeOrg}
            roster={roster}
            loading={loadingContent}
            canManage={Boolean(isOwner)}
            onOpenPerson={setProfileId}
            onGiveTask={(studentId) => setTaskEditor({ task: null, studentIds: [studentId] })}
          />
        )}

        {activeOrg && view === 'groups' && (
          <GroupsView
            org={activeOrg}
            roster={roster}
            canManage={Boolean(isOwner || activeOrg.myKind === 'teacher' || activeOrg.myKind === 'curator')}
            viewerIsStudent={Boolean(isStudent)}
            onOpenBoard={onOpenBoard}
          />
        )}

        {activeOrg && view === 'homework' && (
          <HomeworkView
            org={activeOrg}
            tasks={tasks}
            loading={loadingContent}
            showsAuthors={Boolean(isOwner)}
            canCreate={assignableStudents.length > 0}
            onCreate={() => setTaskEditor({ task: null })}
            onOpenTask={setOpenTaskId}
          />
        )}

        {activeOrg && view === 'myTasks' && (
          <MyTasksView
            org={activeOrg}
            items={myTasks}
            loading={loadingContent}
            onOpenTask={setOpenTaskId}
            onToggleDone={setAssignmentDone}
          />
        )}

        {activeOrg && view === 'audit' && (
          <section className="dash-content">
            <header className="dash-header">
              <div>
                <h1>Журнал</h1>
                <p className="dash-subtitle">
                  Кто что создал, удалил и нарисовал в «{activeOrg.name}»
                </p>
              </div>
            </header>
            <AuditTab orgId={activeOrg.id} />
          </section>
        )}

        {view === 'admin' && (
          <AdminPanel
            onOpenOrg={(id) => {
              setOrgId(id);
              setView('boards');
            }}
          />
        )}

        {activeOrg && view === 'calendar' && (
          <CalendarView
            org={activeOrg}
            events={events}
            boards={boards}
            taskDues={taskDues}
            // A deadline in the calendar is a way *into* the assignment, so
            // it opens the task itself rather than sending anyone to a list.
            onOpenTask={setOpenTaskId}
            loading={loadingContent}
            month={month}
            selectedDay={selectedDay}
            reminders={reminders}
            canCreateEvent={canCreateEvent}
            canEditEvent={canEditEvent}
            viewerIsStudent={Boolean(isStudent)}
            onMonthChange={setMonth}
            onSelectDay={setSelectedDay}
            onOpenDay={setDayModal}
            onCreateEvent={(day) => setEditing({ event: null, day })}
            onEditEvent={(event) => setEditing({ event, day: new Date(event.start) })}
            onToggleTask={toggleTask}
            onToggleEventDone={toggleEventDone}
            onToggleHomeworkStudent={toggleHomeworkStudent}
            viewerUserId={user?.id ?? null}
            canManageHomework={Boolean(isOwner || isTeacher)}
            onMoveEvent={moveEvent}
            onMoveTaskDue={moveTaskDue}
            onOpenBoard={onOpenBoard}
          />
        )}
      </main>

      {dayModal && activeOrg && (
        <DayModal
          day={dayModal}
          events={dayModalEvents}
          dues={dayModalDues}
          boards={boards}
          canCreateEvent={canCreateEvent}
          canEditEvent={canEditEvent}
          viewerIsStudent={Boolean(isStudent)}
          viewerUserId={user?.id ?? null}
          canManageHomework={Boolean(isOwner || isTeacher)}
          onCreateEvent={(day) => {
            setDayModal(null);
            setEditing({ event: null, day });
          }}
          onEditEvent={(event) => {
            setDayModal(null);
            setEditing({ event, day: new Date(event.start) });
          }}
          onOpenTask={(taskId) => {
            setDayModal(null);
            setOpenTaskId(taskId);
          }}
          onOpenBoard={onOpenBoard}
          onToggleEventDone={toggleEventDone}
          onToggleHomeworkStudent={toggleHomeworkStudent}
          onClose={() => setDayModal(null)}
        />
      )}

      {openTask && (
        <TaskModal
          task={openTask.task}
          myDone={openTask.myDone}
          myItem={openTask.item}
          {...(openTask.item
            ? {
                onToggleDone: (done: boolean) =>
                  openTask.item ? setAssignmentDone(openTask.item, done) : undefined,
                onSubmissionChange: (patch: Partial<MyTaskItem>) => {
                  if (openTask.item) patchMySubmission(openTask.item.assignmentId, patch);
                },
              }
            : {})}
          {...(openTask.editable &&
          (isOwner || openTask.editable.teacherId === user?.id)
            ? {
                onEdit: () => {
                  setOpenTaskId(null);
                  setTaskEditor({ task: openTask.editable });
                },
              }
            : {})}
          {...(openTask.editable
            ? {
                onMarkStudent: (studentId: string, done: boolean) =>
                  toggleHomeworkStudent(openTask.task.id, studentId, done),
              }
            : {})}
          onOpenBoard={(boardId) => {
            setOpenTaskId(null);
            // Solution boards are created outside the usual "New board" path,
            // so refresh the list before jumping in — otherwise the board exists
            // on the server but is missing from the sidebar until the next reload.
            if (orgId) void refreshContent(orgId).catch(() => undefined);
            onOpenBoard(boardId);
          }}
          onClose={() => setOpenTaskId(null)}
        />
      )}

      {profilePerson && activeOrg && (
        <PersonProfileModal
          orgId={activeOrg.id}
          person={profilePerson}
          roster={roster}
          canManage={Boolean(isOwner)}
          onSetKind={setMemberKind}
          onSetTeachers={setStudentTeachers}
          onSetStudents={setTeacherStudents}
          onSetPayment={setPayment}
          onGiveTask={(studentId) => {
            setProfileId(null);
            setTaskEditor({ task: null, studentIds: [studentId] });
          }}
          onOpenTask={(taskId) => {
            setProfileId(null);
            setOpenTaskId(taskId);
          }}
          onOpenBoard={(boardId) => {
            setProfileId(null);
            onOpenBoard(boardId);
          }}
          onClose={() => setProfileId(null)}
        />
      )}

      {taskEditor && activeOrg && (
        <TaskEditor
          task={taskEditor.task}
          students={assignableStudents}
          {...(taskEditor.studentIds ? { initialStudentIds: taskEditor.studentIds } : {})}
          knownSubjects={knownSubjects}
          noStudentsHint={
            isOwner
              ? 'В организации пока нет учеников — пригласите людей и назначьте им роль ученика.'
              : 'За вами пока не закреплены ученики — это делает владелец организации.'
          }
          onSave={saveTask}
          onDelete={deleteTask}
          onClose={() => setTaskEditor(null)}
        />
      )}

      {editing && activeOrg && (
        <EventEditor
          orgId={activeOrg.id}
          event={editing.event}
          day={editing.day}
          boards={boards}
          onSave={saveEvent}
          onDelete={deleteEvent}
          onClose={() => setEditing(null)}
        />
      )}

      {reminders.toasts.length > 0 && (
        <div className="toast-stack" role="status" aria-live="polite">
          {reminders.toasts.map((toast) => (
            <div key={toast.id} className="toast">
              <Icon name="bell" size={16} />
              <div>
                <strong>{toast.title}</strong>
                <span>{toast.body}</span>
              </div>
              <button
                type="button"
                aria-label="Скрыть напоминание"
                onClick={() => reminders.dismiss(toast.id)}
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
